/** Static copy from Audit Practice Manual (2024 Edition).xlsm — sheets Hi / Hs / H2 */

import type {G0Page, GProgrammeRow} from '@/pages/components/audit-program/g-section/constants';
import type {ESummaryObjective, ESummaryPage} from '@/pages/components/audit-program/e-section/constants';
import type {SectionIndexRow} from '@/pages/components/audit-program/SectionIndexView';

export type HSectionPageCode = 'Hi' | 'Hs' | 'H2';

export const H_SECTION_PAGES: HSectionPageCode[] = ['Hi', 'Hs', 'H2'];

export const H_PAGE_TITLES: Record<HSectionPageCode, string> = {
    Hi: 'Index',
    Hs: 'Summary sheet - Other investments',
    H2: 'Audit programme - Other investments',
};

export type HSectionIndexRow = SectionIndexRow & {
    pageCode?: Exclude<HSectionPageCode, 'Hi'>;
};

export const H_SECTION_INDEX_LETTER = 'H';
export const H_SECTION_INDEX_TITLE = 'OTHER INVESTMENTS';

/** TOC from APM Hi sheet; navigable rows point to implemented Program pages. */
export const H_SECTION_INDEX: HSectionIndexRow[] = [
    {id: 'h-1', kind: 'item', num: '1', title: 'Lead schedule'},
    {id: 'h-2', kind: 'item', num: '2', title: 'Audit programme', pageCode: 'H2'},
    {id: 'h-3', kind: 'item', num: '3', title: 'Valuation and verification'},
    {id: 'h-s', kind: 'item', num: 's', title: 'Summary sheet', pageCode: 'Hs'},
    {id: 'h-4', kind: 'item', num: '4', title: ''},
    {id: 'h-5', kind: 'item', num: '5', title: ''},
];

export type HProgrammePage = G0Page;
export type HProgrammeRow = GProgrammeRow;
export type HSummaryObjective = ESummaryObjective;
export type HSummaryPage = ESummaryPage;

export const HS_PAGE: HSummaryPage = {
    title: 'SUMMARY SHEET – OTHER INVESTMENTS',
    objectivesTitle: 'Audit objectives',
    objectives: [
        {
            id: 'obj1',
            num: '1',
            text: 'To ensure that the entity has good title to all investments.',
            defaultKeyTests: '5, 6',
            defaultSuppRef: '',
        },
        {
            id: 'obj2',
            num: '2',
            text: 'To ensure that investments are valued correctly, with provision being made for any impairment.',
            defaultKeyTests: '7, 8, 9, 10, 11, 12',
            defaultSuppRef: '',
        },
        {
            id: 'obj3',
            num: '3',
            text: 'To ensure that all income from investments has been accounted for.',
            defaultKeyTests: '13',
            defaultSuppRef: '',
        },
        {
            id: 'obj4',
            num: '4',
            text: 'To ensure that investments and other instruments are properly classified as either non-current or current assets and treatment is consistent.',
            defaultKeyTests: '14',
            defaultSuppRef: '',
        },
        {
            id: 'obj5',
            num: '5',
            text: 'To confirm that all necessary disclosures concerning investments have been made and that the information is appropriately presented and described.',
            defaultKeyTests: '15',
            defaultSuppRef: '',
        },
    ],
    planningTitle: 'Planning conclusion',
    finalTitle: 'Final conclusion',
    materialityLabel: 'Materiality (HK$)',
    planningNote:
        'I am satisfied that from the tests planned sufficient appropriate evidence can be gained to satisfy the objectives.',
    finalBullets: [
        'the work has been performed in accordance with the audit programme;',
        'the work performed and the results obtained have been adequately documented;',
        'all necessary information has been collected for the presentation and disclosure in the financial statements;',
        'sufficient appropriate evidence has been obtained to support the audit conclusion reached;',
        'in my opinion (subject to matters highlighted on B6 or B9) other investments are fairly stated / not fairly stated as described below:',
    ],
};

export const H2_PAGE: HProgrammePage = {
    title: 'AUDIT PROGRAMME - OTHER INVESTMENTS',
    rows: [
        {id: 'r1', kind: 'section', num: '', text: 'General', level: 0},
        {id: 'r2', kind: 'main', num: '1', text: 'Obtain and check, or prepare, a lead schedule for the current year\'s figures and reconcile this to the general ledger.', level: 0},
        {id: 'r3', kind: 'main', num: '2', text: 'Carry out analytical procedures such as:', level: 0},
        {id: 'r4', kind: 'sub', num: '(a)', text: 'comparison of the current figures with those of prior periods;', level: 1},
        {id: 'r5', kind: 'sub', num: '(b)', text: 'review and comparison of key ratios or other performance indicators.', level: 1},
        {id: 'r6', kind: 'main', num: '3', text: 'Review for items above performance materiality or that are otherwise unusual and verify.', level: 0},
        {id: 'r7', kind: 'main', num: '4', text: 'For financial instruments and derivatives please complete the App 3 for other financial instruments and derivatives.', level: 0},
        {id: 'r8', kind: 'section', num: '', text: 'Ownership', level: 0},
        {id: 'r9', kind: 'main', num: '5', text: 'Inspect documents of title such as share certificates.  Ensure that:', level: 0},
        {id: 'r10', kind: 'sub', num: '(a)', text: 'Details are correctly recorded including name, number of shares, type of shares (or equivalent information if not share based);', level: 1},
        {id: 'r11', kind: 'sub', num: '(b)', text: 'The instrument is in the entity name;', level: 1},
        {id: 'r12', kind: 'sub', num: '(c)', text: 'Where the entity is not the registered owner:', level: 1},
        {id: 'r13', kind: 'sub', num: '-', text: 'the entity has a signed declaration from the said person stating that he/she does not beneficially own the interest;', level: 2},
        {id: 'r14', kind: 'sub', num: '-', text: 'a blank signed share transfer form or equivalent exists;', level: 2},
        {id: 'r15', kind: 'sub', num: '(d)', text: 'All documents of title are held in a secure place;', level: 1},
        {id: 'r16', kind: 'sub', num: '(e)', text: 'Examine stamped bought and sold notes, stamped instruments of transfer or other data supporting transactions such as brokers advices;', level: 1},
        {id: 'r17', kind: 'main', num: '6', text: 'Consider obtaining written confirmation where documents of title are held by a third party.', level: 0},
        {id: 'r18', kind: 'section', num: '', text: 'Cost/valuation', level: 0},
        {id: 'r19', kind: 'main', num: '7', text: 'Vouch additions to supporting documentation.  Ensure that the:', level: 0},
        {id: 'r20', kind: 'sub', num: '(a)', text: 'cost;', level: 1},
        {id: 'r21', kind: 'sub', num: '(b)', text: 'entity name; and', level: 1},
        {id: 'r22', kind: 'sub', num: '(c)', text: 'number of shares, have been accounted for correctly.', level: 1},
        {id: 'r23', kind: 'main', num: '8', text: 'Vouch disposals to supporting documentation: Ensure that:', level: 0},
        {id: 'r24', kind: 'sub', num: '(a)', text: 'sales proceeds have been correctly accounted for;', level: 1},
        {id: 'r25', kind: 'sub', num: '(b)', text: 'profit/loss on disposal has been correctly computed;', level: 1},
        {id: 'r26', kind: 'sub', num: '(c)', text: 'transactions have been properly authorized.', level: 1},
        {id: 'r27', kind: 'main', num: '9', text: 'For listed investments:', level: 0},
        {id: 'r28', kind: 'sub', num: '(a)', text: 'check the market values;', level: 1},
        {id: 'r29', kind: 'sub', num: '(b)', text: 'consider whether any provision for impairment is necessary.', level: 1},
        {id: 'r30', kind: 'main', num: '10', text: 'For unlisted investments:', level: 0},
        {id: 'r31', kind: 'sub', num: '(a)', text: 'obtain a copy of the latest accounts of the investment or other means of considering valuation;', level: 1},
        {id: 'r32', kind: 'sub', num: '(b)', text: 'consider whether any provision for impairment is necessary.', level: 1},
        {id: 'r33', kind: 'sub', num: '(c)', text: 'Where an impairment provision may be required:', level: 1},
        {id: 'r34', kind: 'sub', num: '(i)', text: 'Confirm that impairment has been identified as an accounting estimate on C4 Section 4.8 and work planned on C4.1; or', level: 2},
        {id: 'r35', kind: 'sub', num: '(ii)', text: 'Explain how the approach taken complies with the requirements of HKSA 540 (Revised).', level: 2},
        {id: 'r36', kind: 'main', num: '11', text: 'Check that we have sufficient evidence regarding the valuation of all investments.', level: 0},
        {id: 'r37', kind: 'main', num: '12', text: 'Where there is a policy of revaluation for a class of investments:', level: 0},
        {id: 'r38', kind: 'sub', num: '(a)', text: 'Confirm that valuations have been identified as accounting estimates on C4 Section 4.8 and work planned on C4.1; or', level: 1},
        {id: 'r39', kind: 'sub', num: '(b)', text: 'Explain how the approach taken complies with the requirements of HKSA 540 (Revised).', level: 1},
        {id: 'r40', kind: 'section', num: '', text: 'Income', level: 0},
        {id: 'r41', kind: 'main', num: '13', text: 'Refer to the available evidence (e.g., dividend slips) and check that income and approved income have been correctly accounted for.', level: 0},
        {id: 'r42', kind: 'section', num: '', text: 'Presentation and disclosure', level: 0},
        {id: 'r43', kind: 'main', num: '14', text: 'Consider the nature of investments held and whether they have been correctly classified and their treatments are consistent.', level: 0},
        {id: 'r44', kind: 'main', num: '15', text: 'Check that we have sufficient evidence regarding the disclosure of any complex financial instruments.', level: 0},
        {id: 'r45', kind: 'section', num: '', text: 'Bespoke tests', level: 0},
        {id: 'r46', kind: 'main', num: '16', text: 'Draft and perform any necessary additional tests such as those required by C8.3 and cross reference with the objectives on the summary sheet.', level: 0},
        {id: 'r47', kind: 'section', num: '', text: 'Conclusion', level: 0},
        {id: 'r48', kind: 'main', num: '17', text: 'Consider whether there are any points which need to be included in a letter of representation or letter of comment and record on A5 or A6 as appropriate.', level: 0},
        {id: 'r49', kind: 'main', num: '18', text: 'Review the planned extent of reliance on internal controls in this area and consider whether this remains appropriate.', level: 0},
        {id: 'r50', kind: 'main', num: '19', text: 'Assess whether the initial materiality and/or risk assessment should be revised in view of the audit evidence obtained.  Record details of any necessary adjustments on B6 or B9.  Consider the impact on the remainder of the audit work and on any work undertaken to date.', level: 0},
    ],
};
